/**
 * Loop maths for the music director (OD-022 + the owner's spec 16.9.2026). Pure, no Web Audio and no imports, so the same
 * arithmetic the director schedules with can be checked outside a browser (var/tools/music-exit-check.ts).
 *
 * Positions are SAMPLES on the decoded track: the intro runs 0 → loopStart, the loop repeats [loopStart, loopEnd), and an
 * exit marker is a sample where leaving the loop is musically allowed. Empty exitSamples means the loop's own end is the
 * only exit — which is exactly "let the loop go and play out" with no special case.
 */
export type LoopSpec = { sampleRate: number; loopStartSample: number; loopEndSample: number; exitSamples: number[]; outroStartSample: number };

/** Where a source started at sample 0 with the loop armed is now, `elapsed` seconds later. */
export function positionSample(spec: LoopSpec, elapsedSeconds: number): number {
  const s = Math.max(0, Math.round(elapsedSeconds * spec.sampleRate));
  if (s < spec.loopEndSample) return s;                                  // intro, or the first pass through the loop
  const span = spec.loopEndSample - spec.loopStartSample;
  if (span <= 0) return spec.loopEndSample;                              // a broken spec never loops; leave at its end
  return spec.loopStartSample + ((s - spec.loopEndSample) % span);
}

/** How many samples from `pos` the next allowed exit is; wraps into the next loop cycle when this one has none left. */
export function samplesToNextExit(spec: LoopSpec, pos: number): number {
  const span = spec.loopEndSample - spec.loopStartSample;
  const exits = (spec.exitSamples.length ? spec.exitSamples : [spec.loopEndSample]).slice().sort((a, b) => a - b);
  const ahead = exits.find((e) => e >= pos);
  if (ahead !== undefined) return ahead - pos;
  if (span <= 0) return Math.max(0, spec.loopEndSample - pos);
  const inLoop = exits.find((e) => e >= spec.loopStartSample) ?? spec.loopEndSample;   // the first exit of the next cycle
  return (spec.loopEndSample - pos) + (inLoop - spec.loopStartSample);
}

/** Seconds from now to the next exit, for scheduling against AudioContext.currentTime. */
export function secondsToNextExit(spec: LoopSpec, elapsedSeconds: number): number {
  return samplesToNextExit(spec, positionSample(spec, elapsedSeconds)) / spec.sampleRate;
}
